import { Observable, of } from 'rxjs';
import { ComponentDataSourceService } from '../component-data-source.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, catchError } from 'rxjs/operators';
import { LoadDataParams } from '../../../interfaces/DataSource/DataSourceParams.interface';
import { UserDTO } from '../../../dto/users/user.dto';
import { User } from '../../../models/user/User.model';

export function brugere_status(
  componentDataSourceService: ComponentDataSourceService,
  http: HttpClient,
  router: Router
) {
    return {
      name: 'Brugere',
      description: 'Liste af alle brugere og deres status',
      execute: (params?: LoadDataParams) => {
        
        return componentDataSourceService['loadDataWithStandardFiltersGET']('brugere_status', '/users/with-progress', params).pipe(
          catchError(error => {
            console.error('Error fetching users:', error);
            return of({ items: [], totalCount: 0 });
          }),
          map(result => {
            if (result && Array.isArray(result.items)) {
              try {
                const uniqueUserDTOs = result.items.reduce((acc: UserDTO[], userDto: UserDTO) => {
                  if (!acc.some(existing => existing.uuid === userDto.uuid)) {
                    acc.push(userDto);
                  }
                  return acc;
                }, []);

                const users = uniqueUserDTOs.map((userDto: UserDTO) => User.fromDTO(userDto));

                return {
                  items: users,
                  totalCount: result.totalCount,
                  pageSize: result.pageSize,
                  pageIndex: result.pageIndex,
                  filters: result.filters
                };
              } catch (error) {
                console.error('Error processing users:', error);
                return { items: [], totalCount: 0 };
              }
            }
            console.error('[brugere_status] Unexpected API response shape:', result);
            return { items: [], totalCount: 0 };
          })
        );
      },
      settings: {
        /** CMS: use `DunaListUsersComponent` instead of dynamic-table for list UI */
        listComponent: 'DunaListUsersComponent',
        columns: [
          { key: 'name', header: 'Name', type: 'text' },
          { key: 'primaryLocation', header: 'Lokation', type: 'text' },
          { 
            key: 'validation_status', 
            header: 'DUNA Status', 
            type: 'component',
            componentName: 'duna-validation-badge',
            sortable: true,
            componentInputs: (user: any) => {
              // Try multiple ways to access validation_status, similar to educational-path-users.ts
              const validationStatus = user.validation_status || 
                                     user.status?.validation_status || 
                                     'no_data';

              return {
                status: validationStatus,
                customTooltip: getValidationTooltip(validationStatus)
              };
            }
          },
          {
            key: 'educational_paths',
            header: 'Uddannelsesveje',
            type: 'component',
            componentName: 'duna-educational-path-badge',
            componentInputs: (user: any) => {
              const educationalPaths = user.status?.educational_paths || [];
              
              
              return {
                educationalPaths: educationalPaths,
                currentYear: new Date().getFullYear(),
                user: user
              };
            },
            sortable: false,
            alignment: 'center',
            width: '140px'
          },
          {
            key: 'registeredPercentage',
            header: 'Fremgang',
            type: 'component',
            componentName: 'duna-user-progress-label',
            componentInputs: (user: User) => ({
              registeredPercentage: user.status?.registered_percentage ?? 0,
              progressPercentage: user.status?.progress_percentage ?? 0,
              paths: user.status?.educational_paths ?? [],
              totals: {
                maintenance_minutes_required_total:
                  user.status?.maintenance_minutes_required_total,
                maintenance_minutes_completed_total:
                  user.status?.maintenance_minutes_completed_total,
                maintenance_minutes_intended_total:
                  user.status?.maintenance_minutes_intended_total,
                registered_percentage: user.status?.registered_percentage,
                progress_percentage: user.status?.progress_percentage,
              },
              year: new Date().getFullYear(),
            }),
            sortable: true,
            alignment: 'center',
            width: '200px'
          },
          {
            key: 'actions',
            header: '',
            type: 'button',
            buttonText: 'Profil',
            buttonColor: 'primary',
            alignment: 'right',
            variant: 'raised',
            action: (row: User) => {
              router.navigate(['/profile/' + row.uuid]);
            }
          }
        ],
        pageable: true,
        sortable: true,
        rowHover: true,
        searchable: true,
        defaultSortColumn: 'registeredPercentage',
        defaultSortDirection: 'desc'
      }
    }
}

/* -------------------------------------------------------------------------- */
/*  Helper                                                                     */
/* -------------------------------------------------------------------------- */
function getValidationTooltip(status: string): string {
  const lastYear = new Date().getFullYear() - 1;
  
  switch (status) {
    case 'valid':
      return `DUNA krav opfyldt for ${lastYear}`;
    case 'attention':
      return `DUNA kræver opmærksomhed - tjek venligst deltagelse og moduler for ${lastYear}`;
    case 'no_data':
      return `Ingen DUNA data tilgængelig for ${lastYear}`;
    case 'no_paths':
      return 'Ingen uddannelsesveje tildelt';
    default:
      return 'Ukendt DUNA status';
  }
}
